/*
Copyright 2026 Element Creations Ltd.
SPDX-License-Identifier: AGPL-3.0-only OR LicenseRef-Element-Commercial
Please see LICENSE in the repository root for full details.
*/
import { Track, type Participant } from "livekit-client";

export function pictureInPictureSize(
  video: HTMLVideoElement | null,
  screenShare: boolean,
) {
  if (!screenShare || !video?.videoWidth || !video.videoHeight)
    return { width: 640, height: 360 };
  const rotation = Number(video.dataset?.presentationRotation);
  const rotated = rotation === 90 || rotation === 270;
  const width = rotated ? video.videoHeight : video.videoWidth;
  const height = rotated ? video.videoWidth : video.videoHeight;
  const scale = Math.min(1, 960 / Math.max(width, height));
  return {
    width: Math.max(2, Math.round((width * scale) / 2) * 2),
    height: Math.max(2, Math.round((height * scale) / 2) * 2),
  };
}

/** Offscreen carousel tracks must not replace the presentation being watched. */
export function visibleScreenShare(participants: Participant[]) {
  const candidates: {
    participant: Participant;
    video: HTMLVideoElement;
    area: number;
  }[] = [];
  for (const participant of participants) {
    for (const publication of participant.videoTrackPublications.values()) {
      if (
        publication.source !== Track.Source.ScreenShare ||
        publication.isMuted
      )
        continue;
      for (const video of publication.track?.attachedElements ?? []) {
        if (
          !(video instanceof HTMLVideoElement) ||
          video.readyState < 2 ||
          !video.videoWidth
        )
          continue;
        const rect = video.getBoundingClientRect();
        const width = Math.max(
          0,
          Math.min(rect.right, window.innerWidth) - Math.max(rect.left, 0),
        );
        const height = Math.max(
          0,
          Math.min(rect.bottom, window.innerHeight) - Math.max(rect.top, 0),
        );
        if (width * height > 0)
          candidates.push({ participant, video, area: width * height });
      }
    }
  }
  return candidates.sort((a, b) => b.area - a.area)[0] ?? null;
}
